import React, { useState, useEffect } from "react";
import axios from "axios";
import { Loader2, ShieldAlert, LogOut, AlertCircle } from "lucide-react";
import { getBackendUrl } from "../lib/backendUrl";

// Use the configured API origin in local/staging deployments. Production may
// leave this empty when the frontend and API are served behind one domain.
const BACKEND_URL = getBackendUrl();

/**
 * AuthGate — handles 3 states:
 *   null   = checking
 *   true   = authenticated (renders children)
 *   false  = not authenticated (renders login)
 *   "denied" = allowlist blocked (renders access denied)
 *
 */
export default function AuthGate({ children }) {
  const [authState, setAuthState] = useState(null);
  const [user, setUser] = useState(null);
  const [deniedEmail, setDeniedEmail] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    // Check existing session
    axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true })
      .then(res => { setUser(res.data); setAuthState(true); })
      .catch(() => setAuthState(false));
  }, []);

  // Listen for mid-session 401s from any API call
  useEffect(() => {
    const handler = () => {
      setUser(null);
      setAuthState(false);
      setErrorMsg("Your session has expired. Please sign in again.");
    };
    window.addEventListener("capital360:auth-expired", handler);
    return () => window.removeEventListener("capital360:auth-expired", handler);
  }, []);

  const handleLogout = async () => {
    try { await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true }); } catch {}
    setAuthState(false);
    setUser(null);
    setDeniedEmail("");
    setErrorMsg("");
  };

  if (authState === null) {
    return (
      <div className="fixed inset-0 bg-[#0A0A0A] flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="animate-spin text-[var(--brand)]" size={28} />
        </div>
      </div>
    );
  }

  if (authState === "denied") {
    return (
      <div className="fixed inset-0 bg-[#0A0A0A] flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-[var(--bg-surface)] border border-red-500/20 rounded-lg p-6 text-center">
          <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
            <ShieldAlert size={22} className="text-red-400" />
          </div>
          <h1 className="text-lg font-bold text-white mb-2" style={{ fontFamily: "Chivo" }}>Access Restricted</h1>
          <p className="text-sm text-[var(--text-muted)] mb-1">
            <span className="font-mono text-white">{deniedEmail}</span> is not authorized to access Capital360.
          </p>
          <p className="text-[11px] text-[var(--text-muted)] mb-6">This is a private portfolio system. Contact the administrator if you need access.</p>
          <button onClick={handleLogout} className="w-full bg-white/5 hover:bg-white/10 text-white border border-white/10 rounded-md px-4 py-2 text-sm flex items-center justify-center gap-2" data-testid="auth-denied-logout">
            <LogOut size={14} /> Sign out and try another account
          </button>
        </div>
      </div>
    );
  }

  if (authState === false) {
    return (
      <div className="fixed inset-0 bg-[#0A0A0A] flex items-center justify-center p-6">
        <div className="max-w-md w-full text-center">
          <div className="mb-6">
            <h1 className="text-4xl font-bold text-white tracking-tight" style={{ fontFamily: "Chivo" }}>Capital360</h1>
            <p className="text-xs tracking-[0.2em] uppercase text-[var(--text-muted)] mt-1">From Signals to Decisions</p>
          </div>
          <div className="bg-[var(--bg-surface)] border border-white/10 rounded-lg p-6">
            <p className="text-sm text-[var(--text-secondary)] mb-5">Your personal portfolio intelligence system.</p>
            {errorMsg && (
              <div className="mb-4 bg-red-500/10 border border-red-500/20 rounded-md p-3 flex items-start gap-2 text-left" data-testid="auth-error">
                <AlertCircle size={14} className="text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-[11px] text-red-300 leading-relaxed">{errorMsg}</p>
              </div>
            )}
            <LoginTabs onSuccess={(u) => { setUser(u); setAuthState(true); }} />
            <p className="text-[10px] text-[var(--text-muted)] mt-4">Private portfolio application</p>
          </div>
        </div>
      </div>
    );
  }

  // Authenticated — render children with user context
  return (
    <AuthContext.Provider value={{ user, logout: handleLogout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const AuthContext = React.createContext({ user: null, logout: () => {} });


function EmailOtpLogin({ onSuccess }) {
  const [step, setStep] = React.useState("email"); // email | code
  const [email, setEmail] = React.useState("");
  const [code, setCode] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [info, setInfo] = React.useState("");

  const requestOtp = async () => {
    if (!email) return;
    setLoading(true); setError(""); setInfo("");
    try {
      const res = await axios.post(`${BACKEND_URL}/api/auth/otp/request`, { email }, { withCredentials: true });
      setInfo(res.data.message || "Code sent to your email.");
      setStep("code");
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to send code. Please try again.");
    }
    setLoading(false);
  };

  const verifyOtp = async () => {
    if (!code || code.length !== 6) { setError("Enter the 6-digit code."); return; }
    setLoading(true); setError("");
    try {
      const res = await axios.post(`${BACKEND_URL}/api/auth/otp/verify`, { email, code }, { withCredentials: true });
      onSuccess(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Wrong or expired code.");
    }
    setLoading(false);
  };

  return (
    <div className="space-y-2 text-left" data-testid="otp-login">
      {step === "email" ? (
        <>
          <label className="block text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-1.5">Email</label>
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            onKeyDown={e => e.key === "Enter" && requestOtp()}
            placeholder="you@example.com"
            autoFocus
            className="w-full bg-[var(--bg-base)] border border-white/10 rounded-md px-3 py-2.5 text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--brand)]/40"
            data-testid="otp-email-input"
          />
          <button
            onClick={requestOtp}
            disabled={loading || !email}
            className="w-full bg-[var(--brand)] hover:bg-[var(--brand-hover)] text-white rounded-md px-4 py-2.5 text-sm font-medium disabled:opacity-40 transition-colors"
            data-testid="otp-send-btn"
          >
            {loading ? "Sending..." : "Email me a login code"}
          </button>
        </>
      ) : (
        <>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">6-digit code</label>
            <button onClick={() => { setStep("email"); setCode(""); setError(""); setInfo(""); }} className="text-[10px] text-[var(--brand)] hover:underline">← change email</button>
          </div>
          <input
            type="text"
            inputMode="numeric"
            maxLength={6}
            value={code}
            onChange={e => setCode(e.target.value.replace(/\D/g, ""))}
            onKeyDown={e => e.key === "Enter" && verifyOtp()}
            placeholder="______"
            autoFocus
            className="w-full bg-[var(--bg-base)] border border-white/10 rounded-md px-3 py-2.5 text-lg font-mono tracking-[8px] text-center text-white focus:outline-none focus:border-[var(--brand)]/40"
            data-testid="otp-code-input"
          />
          <button
            onClick={verifyOtp}
            disabled={loading || code.length !== 6}
            className="w-full bg-[var(--brand)] hover:bg-[var(--brand-hover)] text-white rounded-md px-4 py-2.5 text-sm font-medium disabled:opacity-40"
            data-testid="otp-verify-btn"
          >
            {loading ? "Verifying..." : "Verify & sign in"}
          </button>
          <button
            onClick={requestOtp}
            disabled={loading}
            className="w-full text-[10px] text-[var(--text-muted)] hover:text-white"
            data-testid="otp-resend-btn"
          >
            Didn't get it? Resend
          </button>
        </>
      )}
      {info && <div className="text-[11px] text-emerald-400 text-center mt-2" data-testid="otp-info">{info}</div>}
      {error && <div className="text-[11px] text-red-400 text-center mt-2" data-testid="otp-error">{error}</div>}
    </div>
  );
}


function LoginTabs({ onSuccess }) {
  const [tab, setTab] = React.useState("password"); // password | otp
  return (
    <div data-testid="login-tabs">
      <div className="flex bg-[var(--bg-base)] border border-white/10 rounded-md p-0.5 mb-4">
        <button
          onClick={() => setTab("password")}
          className={`flex-1 text-[11px] uppercase tracking-wider py-1.5 rounded transition-colors ${tab === "password" ? "bg-[var(--brand)]/15 text-white" : "text-[var(--text-muted)] hover:text-white"}`}
          data-testid="tab-password"
        >Password</button>
        <button
          onClick={() => setTab("otp")}
          className={`flex-1 text-[11px] uppercase tracking-wider py-1.5 rounded transition-colors ${tab === "otp" ? "bg-[var(--brand)]/15 text-white" : "text-[var(--text-muted)] hover:text-white"}`}
          data-testid="tab-otp"
        >Email Code</button>
      </div>
      {tab === "password" ? <PasswordLogin onSuccess={onSuccess} /> : <EmailOtpLogin onSuccess={onSuccess} />}
    </div>
  );
}


function PasswordLogin({ onSuccess }) {
  const [username, setUsername] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [showPw, setShowPw] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");

  const submit = async () => {
    if (!username || !password) {
      setError("Username and password are required.");
      return;
    }
    setLoading(true); setError("");
    try {
      const res = await axios.post(`${BACKEND_URL}/api/auth/login`, { username, password }, { withCredentials: true });
      onSuccess(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Login failed.");
    }
    setLoading(false);
  };

  return (
    <div className="space-y-2 text-left" data-testid="password-login">
      <label className="block text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-1.5">Username</label>
      <input
        type="text"
        value={username}
        onChange={e => setUsername(e.target.value)}
        onKeyDown={e => e.key === "Enter" && submit()}
        placeholder="amansaluja"
        autoFocus
        autoComplete="username"
        className="w-full bg-[var(--bg-base)] border border-white/10 rounded-md px-3 py-2.5 text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--brand)]/40"
        data-testid="password-username-input"
      />
      <label className="block text-[10px] uppercase tracking-wider text-[var(--text-muted)] mt-2 mb-1.5">Password</label>
      <div className="relative">
        <input
          type={showPw ? "text" : "password"}
          value={password}
          onChange={e => setPassword(e.target.value)}
          onKeyDown={e => e.key === "Enter" && submit()}
          placeholder="••••••••"
          autoComplete="current-password"
          className="w-full bg-[var(--bg-base)] border border-white/10 rounded-md px-3 py-2.5 pr-9 text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--brand)]/40"
          data-testid="password-password-input"
        />
        <button
          type="button"
          onClick={() => setShowPw(!showPw)}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--text-muted)] hover:text-white text-[10px] uppercase tracking-wider"
          data-testid="password-toggle-visibility"
        >{showPw ? "Hide" : "Show"}</button>
      </div>
      <button
        onClick={submit}
        disabled={loading || !username || !password}
        className="w-full bg-[var(--brand)] hover:bg-[var(--brand-hover)] text-white rounded-md px-4 py-2.5 text-sm font-medium mt-2 disabled:opacity-40"
        data-testid="password-submit-btn"
      >{loading ? "Signing in..." : "Sign in"}</button>
      <p className="text-[10px] text-[var(--text-muted)] text-center mt-1.5">Stays signed in for 30 days on this device.</p>
      {error && <div className="text-[11px] text-red-400 text-center mt-2" data-testid="password-error">{error}</div>}
    </div>
  );
}
